import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus } from "lucide-react";
import { trpc, type RouterOutputs } from "@/hooks/trpc";
import { toast } from "sonner";
import { TypeList } from "./type-list";
import { EquipmentCard } from "./equipment-card";

type EquipmentParam = RouterOutputs["food"]["equipment"]["listParameters"][number];

export function EquipmentSection() {
  const utils = trpc.useUtils();
  const { data: equipment = [] } = trpc.food.equipment.listWithParams.useQuery();
  const { data: allParameters = [] } = trpc.food.equipment.listParameters.useQuery();

  const createEquipment = trpc.food.equipment.create.useMutation({
    onSuccess: () => utils.food.equipment.listWithParams.invalidate(),
    onError: (e) => toast.error(e.message),
  });
  const deleteEquipment = trpc.food.equipment.delete.useMutation({
    onSuccess: () => utils.food.equipment.listWithParams.invalidate(),
    onError: (e) => toast.error(e.message),
  });

  const createParameter = trpc.food.equipment.createParameter.useMutation({
    onSuccess: () => utils.food.equipment.listParameters.invalidate(),
    onError: (e) => toast.error(e.message),
  });
  const deleteParameter = trpc.food.equipment.deleteParameter.useMutation({
    onSuccess: () => {
      utils.food.equipment.listParameters.invalidate();
      utils.food.equipment.listWithParams.invalidate();
    },
    onError: (e) => toast.error(e.message),
  });

  const [newEquipmentName, setNewEquipmentName] = useState("");
  const equipmentNameRef = useRef<HTMLInputElement>(null);

  const handleAddEquipment = () => {
    if (!newEquipmentName.trim()) return;
    createEquipment.mutate({ name: newEquipmentName.trim() });
    setNewEquipmentName("");
    equipmentNameRef.current?.focus();
  };

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        <div className="space-y-0.5">
          <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Equipment
          </h2>
          <p className="text-xs text-muted-foreground">
            Equipment and tools used during recipe preparation. Assign parameters to define what values must be recorded during cooking.
          </p>
        </div>

        {equipment.length === 0 && (
          <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
            No equipment yet. Add one below.
          </div>
        )}

        {equipment.map((eq) => (
          <EquipmentCard
            key={eq.id}
            equipment={eq}
            allParameters={allParameters}
            onDelete={() => deleteEquipment.mutate(eq.id)}
            isDeleting={deleteEquipment.isPending}
          />
        ))}

        <div className="flex gap-2 pt-1">
          <Input
            ref={equipmentNameRef}
            value={newEquipmentName}
            onChange={(e) => setNewEquipmentName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddEquipment(); } }}
            placeholder="e.g., Oven"
            className="h-8 flex-1"
          />
          <Button
            size="sm"
            variant="outline"
            className="h-8"
            disabled={!newEquipmentName.trim() || createEquipment.isPending}
            onClick={handleAddEquipment}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add
          </Button>
        </div>
      </div>

      <div className="space-y-3">
        <div className="space-y-0.5">
          <h2 className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Parameters
          </h2>
          <p className="text-xs text-muted-foreground">
            Reusable parameters (e.g. Temperature, Time) that can be assigned to any equipment.
          </p>
        </div>
        <TypeList<EquipmentParam>
          items={allParameters}
          secondaryKey="unit"
          secondaryPlaceholder="Unit (e.g. °C)"
          namePlaceholder="e.g., Temperature"
          onAdd={(name, unit) => createParameter.mutate({ name, unit: unit || null })}
          onDelete={(id) => deleteParameter.mutate(id)}
          isAdding={createParameter.isPending}
          isDeleting={deleteParameter.isPending}
        />
      </div>
    </div>
  );
}
